import React, { Fragment, useRef, useLayoutEffect, useState } from "react";
import { TooltipProps } from "recharts";
import styled from "styled-components";
import { UI_CAPTION_BOLD_DF, UI_CAPTION_DF, clampText, colors2 } from "oolib";
import { CountVizLabels } from "../types";
import { LegendTooltipGlyph } from "../Legend/LegendTooltipGlyph";
import { icons } from "oolib";
const { X } = icons;

const UI_CAPTION_DF_WITH_LINE_CLAMP = styled(UI_CAPTION_DF)``;

// =============================================================================
// CONSTANTS
// =============================================================================

/** Offset from cursor position (px) */
const CURSOR_OFFSET = 10;
/** Padding from viewport edge (px) */
const VIEWPORT_PADDING = 5;

/**
 * Max segments listed in the combined "Others" pie tooltip before it collapses
 * into a "+ N more" line. The tooltip follows the cursor and is pointer-events:
 * none, so it can't be scrolled — an unbounded list would run off screen.
 */
const MAX_OTHERS_TOOLTIP_SEGMENTS = 15;

// =============================================================================
// TYPES
// =============================================================================

interface CustomTooltipProps extends TooltipProps<number, string> {
  value?: CountVizLabels[];
  showPercent?: boolean;
  activeBar?: string;
  breakdownCategoryName?: string;
  mousePosition?: {
    x: number;
    y: number;
  };
}

// =============================================================================
// TOOLTIP COMPONENT
// =============================================================================
// Uses transform-only positioning for GPU acceleration (60fps smooth).
// Position is set via left:0, top:0 with transform: translate(x, y).

const CustomTooltip: React.FC<CustomTooltipProps> = (props) => {
  const {
    value,
    showPercent,
    mousePosition,
    activeBar,
    breakdownCategoryName,
  } = props;
  const isActive = props.active;

  const tooltipWrapperRef = useRef<HTMLDivElement>(null);

  // Track Y offset adjustment for overflow correction
  const [yAdjustment, setYAdjustment] = useState(0);

  // ---------------------------------------------------------------------------
  // Overflow detection (runs after render to measure actual tooltip height)
  // ---------------------------------------------------------------------------
  useLayoutEffect(() => {
    if (!tooltipWrapperRef.current || !mousePosition) {
      setYAdjustment(0);
      return;
    }

    const tooltipRect = tooltipWrapperRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // Check if tooltip overflows bottom of viewport
    if (tooltipRect.bottom > windowHeight) {
      // Calculate how much to pull up (negative offset)
      const overflow = tooltipRect.bottom - windowHeight + VIEWPORT_PADDING;
      setYAdjustment(-overflow);
    } else {
      setYAdjustment(0);
    }
  }, [mousePosition?.x, mousePosition?.y]);

  // ---------------------------------------------------------------------------
  // Calculate transform values
  // ---------------------------------------------------------------------------
  const translateX = (mousePosition?.x || 0) + CURSOR_OFFSET;
  const translateY = (mousePosition?.y || 0) + CURSOR_OFFSET + yAdjustment;

  // ---------------------------------------------------------------------------
  // Styles (GPU-accelerated via transform-only positioning)
  // ---------------------------------------------------------------------------
  const style: React.CSSProperties = {
    position: "fixed",
    // Anchor at origin - all positioning via transform (GPU accelerated)
    left: 0,
    top: 0,
    // Use translate3d for GPU layer promotion
    transform: `translate3d(${translateX}px, ${translateY}px, 0)`,
    // Hint to browser that transform will change frequently
    willChange: "transform",
    backgroundColor: colors2.grey80,
    color: colors2.white,
    border: `1px solid ${colors2.grey10}`,
    borderRadius: "6px",
    padding: "8px",
    boxShadow: "0px 4px 10px 0px rgba(0, 0, 0, 0.12)",
    zIndex: 100000,
    width: "max-content",
    maxWidth: "300px",
    maxHeight: "500px",
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    // Prevent tooltip from interfering with mouse events on chart
    pointerEvents: "none",
  };

  if (!isActive) return null;

  return (
    <Fragment>
      <div ref={tooltipWrapperRef} style={style}>
        {/* <UI_HELPTEXT>click to persist tooltip</UI_HELPTEXT> */}
        {breakdownCategoryName && (
          <UI_CAPTION_BOLD_DF>{breakdownCategoryName}</UI_CAPTION_BOLD_DF>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: showPercent ? "auto auto auto" : "auto auto",
            gap: "0.6rem",
          }}
        >
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            {value.map((entry, index) => (
              <div key={`count-${entry.name}-${index}`}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.6rem",
                  }}
                >
                  <LegendTooltipGlyph color={entry.barColor} />
                  <UI_CAPTION_BOLD_DF>
                    {showPercent ? entry.percentage + "%" : entry.count || "0"}
                  </UI_CAPTION_BOLD_DF>
                </div>
              </div>
            ))}
          </div>

          {showPercent && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
            >
              {value.map((entry, index) => (
                <div key={`percent-${entry.name}-${index}`}>
                  <UI_CAPTION_DF key={index}>
                    {entry.count || "0"}
                  </UI_CAPTION_DF>
                </div>
              ))}
            </div>
          )}

          <div
            style={{
              paddingLeft: "0.8rem",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            {value.map((entry, index) => (
              <div
                key={`label-${entry.name}-${index}`}
              // onMouseOver={() => setTooltipTextClampStates(prev => {
              //   const newState = [...prev];
              //   newState[index] = false;
              //   return newState
              // })}
              // onMouseOut={() => setTooltipTextClampStates(prev => {
              //   const newState = [...prev];
              //   newState[index] = true;
              //   return newState
              // })}
              // ref={(el) => (tooltipLabelLengthRefs.current[index] = el)}
              >
                <UI_CAPTION_DF_WITH_LINE_CLAMP>
                  {entry.tooltipLabel}
                </UI_CAPTION_DF_WITH_LINE_CLAMP>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default CustomTooltip;

const StyledLegendGlyph = styled.div<{ $color: string; $borderRadius: string }>`
  width: 8px;
  border-radius: ${({ $borderRadius }) => $borderRadius};
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
  align-self: stretch;
`;

const LegendGlyph: React.FC<{
  color: string;
  borderRadius: string;
}> = ({ color, borderRadius }) => (
  <StyledLegendGlyph $color={color} $borderRadius={borderRadius} />
);

interface TextItem {
  text: string | number;
  bold?: boolean;
  crossAfter?: boolean;
}

interface RowData {
  color: string;
  glyphRadius: string;
  items: TextItem[];
}

const StyledCrossIcon = styled.div`
  display: flex;
  align-items: center;
`;

const renderTextItems = (items: TextItem[], stacked?: boolean) =>
  items.map((item, i) => (
    <Fragment key={i}>
      {item.bold
        ? <UI_CAPTION_BOLD_DF style={!stacked ? { whiteSpace: 'nowrap' } : undefined}>{item.text}</UI_CAPTION_BOLD_DF>
        : <UI_CAPTION_DF>{item.text}</UI_CAPTION_DF>}
      {item.crossAfter && <StyledCrossIcon>
        <X size={12} />
      </StyledCrossIcon>}
    </Fragment>
  ));

const StyledTooltipContainer = styled.div<{ $gap: string }>`
  padding: 1rem;
  max-width: 300px;
  word-break: break-word;
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap};
`;

const StyledTooltipHeader = styled.div`
  margin-bottom: 2px;
`;

const StyledTooltipRow = styled.div<{ $stacked?: boolean }>`
  display: flex;
  align-items: ${({ $stacked }) => $stacked ? 'stretch' : 'flex-start'};
  gap: ${({ $stacked }) => $stacked ? '0.8rem' : '0.6rem'};
`;

const StyledGlyphWrapper = styled.div<{ $stacked?: boolean }>`
  align-self: stretch;
  display: flex;
  padding: ${({ $stacked }) => $stacked ? '0' : '1px 0'};
`;

const StyledStackedTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.6rem;
  justify-content: center;
`;

const TooltipLayout: React.FC<{
  header?: string | number;
  rows: RowData[];
  gap: string;
  stacked?: boolean;
  footer?: string;
}> = ({ header, rows, gap, stacked, footer }) => (
  <StyledTooltipContainer $gap={gap}>
    {header !== undefined && (
      <StyledTooltipHeader>
        <UI_CAPTION_BOLD_DF>{header}</UI_CAPTION_BOLD_DF>
      </StyledTooltipHeader>
    )}
    {rows.map((row, idx) => (
      <StyledTooltipRow key={idx} $stacked={stacked}>
        <StyledGlyphWrapper $stacked={stacked}>
          <LegendGlyph color={row.color} borderRadius={row.glyphRadius} />
        </StyledGlyphWrapper>
        {stacked
          ? <StyledStackedTextWrapper>
              {renderTextItems(row.items, true)}
            </StyledStackedTextWrapper>
          : renderTextItems(row.items)
        }
      </StyledTooltipRow>
    ))}
    {footer && <UI_CAPTION_DF>{footer}</UI_CAPTION_DF>}
  </StyledTooltipContainer>
);

type TooltipContentProps =
  | {
    type: 'bar';
    value: any[];
    breakdownCategoryName?: string;
    // full label of the hovered group, shown at the top when the chart's
    // inline label is faded out (overflowing)
    headerLabel?: string;
  }
  | {
    type: 'heatmap';
    data: {
      count: number;
      percentage?: number;
      name: string;
      tooltipLabel: string;
      barColor: string;
    };
  }
  | {
    type: 'line';
    value: { count: number; tooltipLabel: string; barColor: string }[];
    xAxisLabel?: string | number;
  }
  | {
    // One point on a scatter / bubble chart: its name in bold, then any
    // number of plain detail lines under it.
    type: 'scatter';
    data: { tooltipLabel: string; barColor: string; details: string[] };
  }
  | {
    type: 'pie';
    data: {
      count: number;
      percentage?: number;
      tooltipLabel: string;
      barColor: string;
      level?: number;
      /**
       * Present only on the combined "Others" slice — the segments that were
       * grouped into it, largest first. When set, the tooltip lists them
       * instead of showing a single "Others" row.
       */
      othersSegments?: { display: string; count: number; percentage?: number; barColor?: string }[];
    };
    showPercent?: boolean;
    levelName?: string;
  };

export const TooltipContent: React.FC<TooltipContentProps> = (props) => {
  switch (props.type) {
    case 'bar': {
      const { value, breakdownCategoryName, headerLabel } = props;
      if (!value || value.length === 0) return null;
      const entry = value[0];
      const items: TextItem[] = [];
      if (breakdownCategoryName) items.push({ text: entry.name, bold: true, crossAfter: true });
      items.push({ text: entry.tooltipLabel, bold: true });
      return (
        <TooltipLayout
          stacked
          header={headerLabel}
          gap="0.6rem"
          rows={[{ color: entry.barColor, glyphRadius: '6px', items }]}
        />
      );
    }

    case 'heatmap': {
      const { data } = props;
      if (!data) return null;
      return (
        <TooltipLayout
          stacked
          gap="0.6rem"
          rows={[{
            color: data.barColor,
            glyphRadius: '4px',
            items: [
              { text: data.tooltipLabel, bold: true, crossAfter: true },
              { text: data.name, bold: true },
            ],
          }]}
        />
      );
    }

    case 'line': {
      const { value, xAxisLabel } = props;
      if (!value || value.length === 0) return null;
      return (
        <TooltipLayout
          header={xAxisLabel}
          gap="0.8rem"
          rows={value.map((entry) => ({
            color: entry.barColor,
            glyphRadius: '8px',
            items: [
              { text: String(entry.count ?? 0), bold: true },
              { text: entry.tooltipLabel },
            ],
          }))}
        />
      );
    }

    case 'scatter': {
      const { data } = props;
      if (!data) return null;
      return (
        <TooltipLayout
          stacked
          gap="0.6rem"
          rows={[{
            color: data.barColor,
            glyphRadius: '6px',
            items: [
              { text: data.tooltipLabel, bold: true },
              ...data.details.map((text) => ({ text })),
            ],
          }]}
        />
      );
    }

    case 'pie': {
      const { data, showPercent = true, levelName } = props;
      if (!data) return null;
      const isSunburst = data.level !== undefined && data.level >= 0;

      const makeItems = (d: { count: number; percentage?: number; label: string }) => {
        const items: TextItem[] = [];
        if (showPercent && d.percentage !== undefined) items.push({ text: `${parseFloat(d.percentage.toFixed(2))}%`, bold: true });
        items.push({ text: d.count, bold: true });
        items.push({ text: d.label });
        return items;
      };

      // The combined "Others" slice lists the segments it grouped, rather than a
      // single opaque "Others" row.
      if (data.othersSegments?.length) {
        const shown = data.othersSegments.slice(0, MAX_OTHERS_TOOLTIP_SEGMENTS);
        const hiddenCount = data.othersSegments.length - shown.length;
        return (
          <TooltipLayout
            header={data.tooltipLabel}
            gap="0.6rem"
            rows={shown.map((seg) => ({
              color: seg.barColor || data.barColor,
              glyphRadius: '6px',
              items: makeItems({ count: seg.count, percentage: seg.percentage, label: seg.display }),
            }))}
            footer={hiddenCount > 0 ? `+ ${hiddenCount} more` : undefined}
          />
        );
      }

      return (
        <TooltipLayout
          header={isSunburst ? `Lv ${data.level! + 1}${levelName ? `: ${levelName}` : ''}` : undefined}
          gap="0.6rem"
          rows={[{ color: data.barColor, glyphRadius: '6px', items: makeItems({ count: data.count, percentage: data.percentage, label: data.tooltipLabel }) }]}
        />
      );
    }
  }
};

